import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker } from '@material-ui/pickers'
import IconButton from '@material-ui/core/IconButton'
import ClearIcon from '@material-ui/icons/Clear'
import { useField } from 'formik'
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { ErrorMessage } from 'components/Shared/ErrorMessage'
import { momentToISOLocalDate } from 'utils/date'
import { useValidationSchema } from 'context/validationSchema'
import { isFieldRequired } from 'components/Shared/FormikForm'

const useStyles = makeStyles({
  defaultWidth: {
    width: '310px'
  },
  tableFilterWidth: {
    width: '130px'
  }
})

export function DatePickerFormFieldInline ({
  label,
  format = 'DD-MM-YYYY',
  name,
  disabled,
  skipIcon = false,
  clearable = false,
  inputClassName,
  datePickerClassName,
  onChange = () => {},
  notRequiredTouchedToError
}) {

  const [, meta, helpers] = useField(name)
  const { value, error, touched } = meta
  const { setValue, setTouched } = helpers
  const { t } = useTranslation()
  const { schema } = useValidationSchema()
  const isRequired = useMemo(() => isFieldRequired(schema, name), [schema, name])
  const classes = useStyles()

  const labelText = t(label)

  function handleChange (momentValue) {
    const newValue = momentToISOLocalDate(momentValue)
    setTouched(true)
    setValue(newValue)
    onChange(newValue)
  }

  const isError = !!error && (touched || notRequiredTouchedToError)

  return (
    <DatePicker
      error={isError}
      helperText={<ErrorMessage name={name} notRequiredTouchedToError={notRequiredTouchedToError}/>}
      variant="inline"
      inputVariant="filled"
      className={datePickerClassName || classes.defaultWidth}
      label={labelText}
      disabled={disabled}
      placeholder={labelText}
      value={value}
      onChange={handleChange}
      format={format}
      autoOk
      onBlur={() => setTouched(true)}
      required={isRequired}
      InputProps={{
        classes: {
          input: inputClassName
        },
        endAdornment: (
          clearable ? (value && !disabled && <ClearButtonIcon onClick={() => {
              setTouched(true)
              setValue(null)
              onChange(null)
            }}/>) :
            skipIcon ? null : <CalendarIcon/>
        ),
        disableUnderline: true
      }}
    />
  )
}

export function DatePickerInline ({
  placeholder,
  value,
  onChange,
  format,
  className = 'defaultWidth',
}) {
  const { t } = useTranslation()
  const classes = useStyles()

  return (
    <DatePicker
      variant="inline"
      disableToolbar
      inputVariant="outlined"
      placeholder={t(placeholder)}
      value={value}
      onChange={onChange}
      format={format}
      autoOk
      className={classes[className]}
      InputProps={{
        endAdornment: (
          value &&
          <ClearButtonIcon onClick={() => onChange(null)}/>
        )
      }}
    />
  )
}

export function ClearButtonIcon ({ onClick }) {
  return (<IconButton size="small"
                      onClick={(event) => {
                        onClick(event)
                        event.stopPropagation()
                      }}>
      <ClearIcon fontSize="inherit"/>
    </IconButton>
  )
}
